// Small popovers that hang off the button that opened them: a card's "…" menu, the split primary's
// ▾, and the assignee picker (docs/design/actions.md, variant A). On a phone (640px and under) the same
// content is a bottom sheet with 44px rows and Cancel. Rendered into <body>, so a card's overflow and
// stacking never clip them. Keyboard: arrows, Home and End move through the entries; Escape closes and
// puts the focus back on the button that opened it; a click outside closes.

import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type KeyboardEvent, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";

const PHONE = "(max-width: 640px)";

/** Whether the screen is a phone's (app.css's 640px): popovers become bottom sheets there. */
export function usePhone(): boolean {
  return useSyncExternalStore(
    (on) => {
      if (typeof window === "undefined" || !window.matchMedia) return () => {};
      const mq = window.matchMedia(PHONE);
      mq.addEventListener("change", on);
      return () => mq.removeEventListener("change", on);
    },
    () => (typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(PHONE).matches),
    () => false,
  );
}

/** What the arrow keys move through: menu entries and picker rows that show. */
const ENTRIES = '[role="menuitem"], [role="menuitemradio"], [role="menuitemcheckbox"]';
const entriesIn = (root: HTMLElement) => [...root.querySelectorAll<HTMLElement>(ENTRIES)].filter((el) => !el.closest("[hidden]"));

/** Arrows, Home and End move the focus through the entries; Down from a search box goes to the first. */
export function moveFocus(root: HTMLElement, e: { key: string; target: EventTarget | null; preventDefault: () => void }) {
  if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
  const t = e.target as HTMLElement | null;
  if (t?.tagName === "TEXTAREA") return;
  if (t?.tagName === "INPUT" && e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
  const xs = entriesIn(root);
  if (!xs.length) return;
  const i = xs.indexOf(document.activeElement as HTMLElement);
  const n = e.key === "Home" ? 0 : e.key === "End" ? xs.length - 1 : i < 0 ? (e.key === "ArrowDown" ? 0 : xs.length - 1) : (i + (e.key === "ArrowDown" ? 1 : -1) + xs.length) % xs.length;
  xs[n]?.focus();
  e.preventDefault();
}

/**
 * A popover anchored under (or, without room, over) `anchor`, right-aligned to it; a bottom sheet on a
 * phone. `sheetHead` is the sheet's first line (the item's number and title). Focus goes to `focus`
 * (a selector inside) or the first entry when it opens.
 */
export function Popover({
  anchor,
  onClose,
  label,
  role = "menu",
  className = "",
  sheetHead,
  focus,
  align = "end",
  children,
}: {
  anchor: RefObject<HTMLElement | null>;
  onClose: () => void;
  label: string;
  role?: "menu" | "dialog";
  className?: string;
  sheetHead?: ReactNode;
  focus?: string;
  /** which edges line up: the button's right (the default) or its left (the board switcher) */
  align?: "start" | "end";
  children: ReactNode;
}) {
  const phone = usePhone();
  const box = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const close = useRef(onClose);
  close.current = onClose;

  // where it goes: under the button, right edges lined up; over it when there is no room below
  useLayoutEffect(() => {
    if (phone) return;
    const place = () => {
      const a = anchor.current?.getBoundingClientRect();
      const b = box.current;
      if (!a || !b) return;
      const w = b.offsetWidth;
      const h = b.offsetHeight;
      const left = Math.max(8, Math.min(align === "start" ? a.left : a.right - w, window.innerWidth - w - 8));
      const below = a.bottom + 4;
      const top = below + h > window.innerHeight - 8 && a.top - h - 4 > 8 ? a.top - h - 4 : below;
      setPos((cur) => (cur && cur.top === top && cur.left === left ? cur : { top, left }));
    };
    place();
    let raf = 0;
    const again = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(place);
    };
    window.addEventListener("scroll", again, true);
    window.addEventListener("resize", again);
    const ro = new ResizeObserver(again);
    if (box.current) ro.observe(box.current);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", again, true);
      window.removeEventListener("resize", again);
      ro.disconnect();
    };
  }, [phone, anchor, align]);

  // the focus goes in when it opens
  useEffect(() => {
    const b = box.current;
    if (!b) return;
    // "a | b": the first of these that is there
    const first = (focus ? focus.split("|").map((sel) => b.querySelector<HTMLElement>(sel.trim())).find(Boolean) : null) ?? entriesIn(b)[0] ?? b;
    first.focus({ preventScroll: true });
  }, [focus, phone]);

  // a click outside closes it (the button that opened it toggles it itself)
  useEffect(() => {
    const down = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (!t || box.current?.contains(t) || anchor.current?.contains(t)) return;
      if (phone && (t as HTMLElement).closest?.(".sheet")) return;
      close.current();
    };
    document.addEventListener("pointerdown", down, true);
    return () => document.removeEventListener("pointerdown", down, true);
  }, [anchor, phone]);

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      // the board's own Escape (close the newest panel) leaves this one alone
      e.preventDefault();
      e.stopPropagation();
      close.current();
      anchor.current?.focus();
      return;
    }
    if (box.current) moveFocus(box.current, e);
  };

  if (typeof document === "undefined") return null;
  if (phone)
    return createPortal(
      <div className="sheet-scrim" onPointerDown={(e) => e.target === e.currentTarget && close.current()}>
        <div className={`sheet ${className}`} role="dialog" aria-modal="true" aria-label={label} ref={box} tabIndex={-1} onKeyDown={onKey}>
          <div className="grab" aria-hidden="true" />
          {sheetHead && <div className="sh-t">{sheetHead}</div>}
          {role === "menu" ? (
            <div role="menu" aria-label={label}>
              {children}
            </div>
          ) : (
            children
          )}
          <button
            type="button"
            className="sh-cancel"
            onClick={() => {
              close.current();
              anchor.current?.focus();
            }}
          >
            Cancel
          </button>
        </div>
      </div>,
      document.body,
    );
  return createPortal(
    <div
      className={`pop-menu ${className}`}
      role={role}
      aria-label={label}
      ref={box}
      tabIndex={-1}
      style={pos ? { top: pos.top, left: pos.left } : { top: -9999, left: -9999 }}
      onKeyDown={onKey}
    >
      {children}
    </div>,
    document.body,
  );
}

/** One entry of a menu. `off`: it shows, greyed, with why it can't be used yet. */
export interface MenuEntry {
  key: string;
  label: string;
  hint?: string;
  off?: string;
  onSelect: () => void;
}
export interface MenuGroup {
  title: string;
  entries: MenuEntry[];
}

/** The entries, grouped (Work / Organize / Admin), a line between groups. */
export function MenuEntries({ groups, onDone }: { groups: MenuGroup[]; onDone: () => void }) {
  return (
    <>
      {groups.map((g, i) => (
        <div role="group" aria-label={g.title} key={g.title}>
          {i > 0 && <div className="cm-sep" role="separator" />}
          <div className="mh" aria-hidden="true">
            {g.title}
          </div>
          {g.entries.map((x) => (
            <button
              key={x.key}
              type="button"
              role="menuitem"
              className="cmi"
              data-key={x.key}
              aria-disabled={x.off ? "true" : undefined}
              title={x.off}
              onClick={() => {
                if (x.off) return;
                onDone();
                x.onSelect();
              }}
            >
              <span className="l">{x.label}</span>
              {(x.off ?? x.hint) && <span className="h">{x.off ?? x.hint}</span>}
            </button>
          ))}
        </div>
      ))}
    </>
  );
}

/** A menu: a popover (a sheet on a phone) of grouped entries. Picking one closes it first. */
export function Menu({
  anchor,
  groups,
  label,
  sheetHead,
  onClose,
}: {
  anchor: RefObject<HTMLElement | null>;
  groups: MenuGroup[];
  label: string;
  sheetHead?: ReactNode;
  onClose: () => void;
}) {
  return (
    <Popover anchor={anchor} onClose={onClose} label={label} className="cmenu" sheetHead={sheetHead}>
      <MenuEntries groups={groups} onDone={onClose} />
    </Popover>
  );
}
